import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import PropType from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';
import { Constants } from '@pwc/pwc-deals-web-common';

import { TABLEAU_LIBRARY_ITEM_TYPES } from 'common/components/SectionArea/libraryItemUtils';
import { UNABLE_TO_ADD_OBJECT } from 'common/components/ModalError/constants';
import { showModalError } from 'common/redux/errors/errorsSlice';
import { PowerBiContainer } from 'common/components/SelectReport/PowerBiContainer';
import { TableauContainer } from 'common/components/SelectReport/TableauContainer';
import {
  clearClipboard,
  getLibraryItemsFromClipboard,
  postRichText,
  toggleLibraryItemFormatForClipboard,
} from 'features/contentLibrary/libraryItemService';
import {
  useDragAndDrop,
  useSections,
} from 'features/analyses/Freeform/FreeformAnalysisForm/FreeformAnalysisFormContext';
import UnableToLoadContent from 'features/analyses/common/UnableToLoadContent/UnableToLoadContent';
import { setCurrentSections } from 'features/analyses/Freeform/FreeformAnalysisForm/FreeformAnalysisFormContextUtils';
import { EmptyClipboardModal } from 'common/components/EmptyClipboardModal';

import DraggableLibraryItemCard from '../../../../../DraggableLibraryItemCard';
import ObjectInjestPopover from '../../../../../ObjectInjestPopover/ObjectInjestPopover';

import './SectionArea.scss';
import CONSTANTS from '../../../../Constants';
import DeleteButton from '../DeleteButton/DeleteButton';
import { getStyle } from './utils';
import { POWER_PIVOT_LIBRARY_ITEM_TYPES } from '../../../../../../../common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes';
import {
  HIDE_LOST_COMMENTS_DIALOG_SESSION_STORAGE,
  LostCommentsWarningDialog,
} from '../../../../../../../../../common/components/LostCommentsWarningDialog';

function SectionArea({
  areaClass,
  droppableLocation,
  droppableLocations,
  hideExcelCellRangeFormatSwitch,
  libraryItemMap,
  libraryItems,
  panelNumber = 0,
  sectionData,
  showRightPanel,
}) {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const [
    isComponentDraggableLibraryItemCardLoading,
    setComponentDraggableLibraryItemCardLoading,
  ] = useState(false);
  const { t } = useTranslation();
  const [popOver, onPopover] = useState(false);
  const [showPowerBIModal, setPowerBIModalDisplay] = useState(false);
  const [noDataOnClipboard, setNoDataOnClipboard] = useState(false);
  const [mouseOver, setMouseOver] = useState(false);
  const [clearSectionHover, setClearSectionHover] = useState(false);
  const [showTableauModal, setShowTableauModal] = useState(false);
  const [
    // eslint-disable-next-line no-unused-vars
    { currentSections = [], nonLaidOutLibraryItems = [] },
    // eslint-disable-next-line no-unused-vars
    contextDispatch,
  ] = useSections();
  const { setDroppableLocations } = useDragAndDrop();

  const [showLostCommentsModal, setShowLostCommentsModal] = useState(false);

  const isLostCommentsModalHidden =
    window.sessionStorage.getItem(HIDE_LOST_COMMENTS_DIALOG_SESSION_STORAGE) ===
    'true';

  const handleExcelCellRangeTypeContentChange = useCallback(
    async ({
      current,
      items: {
        excel: { cellRange: item },
      },
    }) => {
      if (!['image', 'web-table'].includes(current)) {
        return;
      }

      const formatTypeNew = current === 'web-table' ? 'HTML' : 'SVG';

      const newSections = currentSections.reduce(
        (acc, x) => {
          const theSection = cloneDeep(x);

          // set the new FormatType causing rerender
          theSection.items.some((itemLoop) => {
            if (itemLoop.id === item.id) {
              const dataItem = JSON.parse(itemLoop.data);

              if (dataItem.FormatType !== formatTypeNew) {
                dataItem.FormatType = formatTypeNew;
                itemLoop.data = JSON.stringify(dataItem);
                acc.updated = true;

                acc.tasks.push(
                  toggleLibraryItemFormatForClipboard(projectId, item.id, {
                    formatType: formatTypeNew,
                  })
                );

                return true;
              }
            }

            return false;
          });

          acc.sections.push(theSection);

          return acc;
        },
        {
          updated: false,
          tasks: [],
          sections: [],
        }
      );

      if (newSections.updated) {
        setCurrentSections(newSections.sections, contextDispatch);
      }

      if (newSections.tasks.length) {
        try {
          setComponentDraggableLibraryItemCardLoading(true);
          await Promise.all(newSections.tasks);
        } finally {
          setComponentDraggableLibraryItemCardLoading(false);
        }
      }
    },
    [contextDispatch, currentSections, projectId]
  );

  const onAddRichText = async (droppableLocation2) => {
    onPopover(false);
    try {
      const name = uuidv4();
      const richTextData = JSON.stringify({
        RichTextData: '',
      });

      const id = await postRichText(projectId, name, richTextData);
      if (!id) {
        dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
      } else {
        const newSections = cloneDeep(currentSections);

        newSections[sectionData.order - 1].items.push({
          id,
          name,
          type: Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value,
          isShared: false,
          data: richTextData,
          analysisColumn: droppableLocation2.position,
        });

        setCurrentSections(newSections, contextDispatch);

        const newDroppableLocations = cloneDeep(droppableLocations);
        newDroppableLocations[droppableLocation.id] = {
          ...droppableLocation,
          libraryItemIds: [...droppableLocation.libraryItemIds, id],
        };
        setDroppableLocations(newDroppableLocations);
      }
    } catch (e) {
      dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
    }
  };

  const onAddPBIItem = async (PBIItem, droppableLocation2) => {
    onPopover(false);
    setPowerBIModalDisplay(false);

    const newSections = cloneDeep(currentSections);

    newSections[sectionData.order - 1].items.push({
      ...PBIItem,
      analysisColumn: droppableLocation2.position,
    });

    setCurrentSections(newSections, contextDispatch);

    const newDroppableLocations = cloneDeep(droppableLocations);
    newDroppableLocations[droppableLocation.id] = {
      ...droppableLocation,
      libraryItemIds: [...droppableLocation.libraryItemIds, PBIItem.id],
    };
    setDroppableLocations(newDroppableLocations);
  };

  const onAddTableauItem = async (tableauItem, droppableLocation2) => {
    onPopover(false);
    setShowTableauModal(false);

    const newSections = cloneDeep(currentSections);

    const tableauType = tableauItem.allTabsSelected
      ? TABLEAU_LIBRARY_ITEM_TYPES.TableauReport
      : TABLEAU_LIBRARY_ITEM_TYPES.TableauReportPage;

    newSections[sectionData.order - 1].items.push({
      ...tableauItem,
      analysisColumn: droppableLocation2.position,
      id: tableauItem.id,
      type: tableauType,
    });

    setCurrentSections(newSections, contextDispatch);

    const newDroppableLocations = cloneDeep(droppableLocations);
    newDroppableLocations[droppableLocation.id] = {
      ...droppableLocation,
      libraryItemIds: [...droppableLocation.libraryItemIds, tableauItem.id],
    };
    setDroppableLocations(newDroppableLocations);
  };

  const onAddExcelOrPPT = async (droppableLocation2) => {
    try {
      const libraryItemResponse = await getLibraryItemsFromClipboard(projectId);

      if (!libraryItemResponse) {
        setNoDataOnClipboard(true);
        return;
      }

      const { id, data, name, status, type } = libraryItemResponse;

      let libraryItemData = {
        CellRange: data.cellRange,
        DriveId: data.driveId,
        DriveItemId: data.driveItemId,
        GroupedByKey: data.driveId,
        SharePointItemUrl: data.sharePointItemUrl,
        SiteId: data.siteId,
      };

      switch (type) {
        case Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value:
          libraryItemData = {
            ...libraryItemData,
            WorksheetName: data.worksheetName,
            UniqueId: data.uniqueId,
            ChartName: data.chartName,
          };
          break;

        case Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value:
          libraryItemData = {
            ...libraryItemData,
            FormatType: data.formatType,
            Name: data.name,
            UniqueId: data.uniqueId,
            WorksheetName: data.worksheetName,
          };
          break;

        case Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value:
          libraryItemData = {
            ...libraryItemData,
            WorksheetName: data.worksheetName,
            UniqueId: data.uniqueId,
            Name: data.name,
            NamedRange: data?.namedRange,
            CellRange: data.cellRange,
          };
          break;

        case Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value:
          libraryItemData = {
            ...libraryItemData,
            SlideNumber: data.slideNumber,
            GroupName: data.groupName,
          };
          break;

        case POWER_PIVOT_LIBRARY_ITEM_TYPES.DynamicExcelCellRange:
          libraryItemData = {
            ...libraryItemData,
            worksheetName: data.worksheetName,
            uniqueId: data.uniqueId,
            name: data.name,
            embeddedUrl: data.embeddedUrl,
            sharePointItemUrl: data.sharePointItemUrl,
            errorCode: data.errorCode,
          };
          break;

        default:
          break;
      }

      const libraryItemCreated = {
        data: JSON.stringify(libraryItemData),
        formatType: data.formatType,
        id,
        name,
        status,
        type,
      };

      const newSections = cloneDeep(currentSections);

      newSections[sectionData.order - 1].items.push({
        ...libraryItemCreated,
        analysisColumn: droppableLocation2.position,
      });

      setCurrentSections(newSections, contextDispatch);

      const newDroppableLocations = cloneDeep(droppableLocations);
      newDroppableLocations[droppableLocation.id] = {
        ...droppableLocation,
        libraryItemIds: [
          ...droppableLocation.libraryItemIds,
          libraryItemCreated.id,
        ],
      };
      setDroppableLocations(newDroppableLocations);

      // clear the clipboard
      await clearClipboard(projectId, libraryItemResponse);
    } catch (e) {
      dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
    }
  };

  const hideClearSectionButton = () => {
    setMouseOver(false);
  };

  const clearSection = () => {
    const newSections = cloneDeep(currentSections);

    newSections[sectionData.order - 1].items = newSections[
      sectionData.order - 1
    ].items.filter((x) => x.analysisColumn !== droppableLocation.position);
    setCurrentSections(newSections, contextDispatch);

    const newDroppableLocations = cloneDeep(droppableLocations);
    newDroppableLocations[droppableLocation.id] = {
      ...droppableLocation,
      libraryItemIds: [],
    };
    setDroppableLocations(newDroppableLocations);
  };

  return (
    <Droppable
      droppableId={droppableLocation.id}
      direction={droppableLocation.direction}
      key={droppableLocation.id}
      ignoreContainerClipping
    >
      {(provided, snapshot) => {
        const computedStyle = getStyle(
          snapshot,
          droppableLocation.id,
          popOver,
          libraryItemMap,
          libraryItems
        );
        return (
          <div
            className={`${areaClass} section-area__droppable-container`}
            data-testid={areaClass}
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={computedStyle}
            onMouseEnter={() => setMouseOver(true)}
            onFocus={() => setMouseOver(true)}
            onMouseLeave={hideClearSectionButton}
            onBlur={hideClearSectionButton}
          >
            <div
              className={classNames('section-area__item-container', {
                'background-left':
                  droppableLocation.id.includes('text-overflow'),
              })}
            >
              {libraryItems.map((libraryItem, index) => {
                if (noDataOnClipboard) {
                  return (
                    <UnableToLoadContent
                      key={libraryItem.id}
                      projectId={projectId}
                      placeId="content-error-features-sectionArea"
                    />
                  );
                }

                return (
                  <DraggableLibraryItemCard
                    droppableLocationId={droppableLocation.id}
                    droppableLocations={droppableLocations}
                    forView={{
                      payload: {
                        onExcelCellRangeContentTypeChange:
                          handleExcelCellRangeTypeContentChange,
                      },
                      type: 'freeFormAnalysisEdit',
                    }}
                    hideExcelCellRangeFormatSwitch={
                      hideExcelCellRangeFormatSwitch
                    }
                    index={index}
                    key={libraryItem.id}
                    libraryItem={libraryItem}
                    loading={isComponentDraggableLibraryItemCardLoading}
                    panelNumber={panelNumber}
                  />
                );
              })}
              {provided.placeholder}
            </div>

            <div
              className="clearSectionWrapper"
              style={{
                display:
                  mouseOver &&
                  libraryItems.length > 0 &&
                  !snapshot.isDraggingOver &&
                  !showRightPanel
                    ? 'flex'
                    : 'none',
              }}
            >
              <DeleteButton
                tooltip={t('clearSection')}
                tooltipPlacement="top"
                iconType={`${
                  clearSectionHover ? 'fill' : 'outline'
                } icon-grey-theme`}
                handleClick={() => {
                  if (isLostCommentsModalHidden) {
                    clearSection();
                  } else {
                    setShowLostCommentsModal(true);
                  }
                }}
                handleMouseEnter={() => setClearSectionHover(true)}
                handleMouseLeave={() => setClearSectionHover(false)}
              />
              <LostCommentsWarningDialog
                open={showLostCommentsModal}
                onConfirm={() => {
                  setShowLostCommentsModal(false);
                  clearSection();
                }}
                onClose={() => {
                  setShowLostCommentsModal(false);
                }}
              />
            </div>

            {libraryItems.length < 1 && (
              <div
                className={classNames('section-area__background', {
                  'background-left':
                    droppableLocation.id.includes('text-overflow'),
                })}
              >
                <div className="section-area__background__content">
                  <ObjectInjestPopover
                    onInsertText={() => onAddRichText(droppableLocation)}
                    onInsertPowerBi={() => {
                      // TODO: jm - use common modal for powerbi and tableau
                      setPowerBIModalDisplay(true);
                    }}
                    onInsertTableau={() => {
                      // TODO: jm - use common modal for powerbi and tableau
                      setShowTableauModal(true);
                    }}
                    onInsertExcelOrPPT={() =>
                      onAddExcelOrPPT(droppableLocation)
                    }
                    onPopover={onPopover}
                    droppableLocation={droppableLocation}
                    allowTextOnly={
                      CONSTANTS.LAYOUT_TYPE_TO_SECTION_CLASSNAME_MAP[
                        sectionData.layoutType
                      ] === 'one-column-text-overflow-area'
                    }
                  />
                  {showPowerBIModal && (
                    <PowerBiContainer
                      show={showPowerBIModal}
                      onAdd={(PBIItem) =>
                        onAddPBIItem(PBIItem, droppableLocation)
                      }
                      onHide={() => setPowerBIModalDisplay(false)}
                      title="Add a Power BI page"
                    />
                  )}
                  {showTableauModal && (
                    <TableauContainer
                      show={showTableauModal}
                      onAdd={(tableauItem) => {
                        setShowTableauModal(false);
                        onAddTableauItem(tableauItem, droppableLocation);
                      }}
                      onHide={() => setShowTableauModal(false)}
                      title={t('addATableauPage')}
                    />
                  )}
                </div>
              </div>
            )}
            {droppableLocation.id.includes('text-overflow') && (
              <div className="section-area__background background-right" />
            )}
            {noDataOnClipboard && (
              <EmptyClipboardModal
                show={noDataOnClipboard}
                onHide={() => setNoDataOnClipboard(false)}
              />
            )}
          </div>
        );
      }}
    </Droppable>
  );
}

SectionArea.propTypes = {
  areaClass: PropType.string,
};

SectionArea.defaultProps = {
  areaClass: '',
};

export default SectionArea;
